import React from 'react';
import { format, addMinutes, isWithinInterval } from 'date-fns';
import { Video, MessageCircle, Building2, CalendarDays, ClipboardList } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useClinic } from '@/contexts/ClinicContext';
import { useAuth } from '@/contexts/AuthContext';
import { cn } from '@/lib/utils';
import type { EmrAppointment } from '@/services/emrService';
import type { CalendarEvent } from '@/services/calendarService';
import type { Task } from '@/services/taskService';
import VisitActionButton from '@/components/visit/VisitActionButton';

const taskPriorityColors: Record<string, string> = {
  high: '#ef4444',
  medium: '#f97316',
  low: '#eab308',
};

interface DayViewProps {
  date: Date;
  appointments: EmrAppointment[];
  externalEvents?: CalendarEvent[];
  tasks?: Task[];
  onAppointmentClick: (appointment: EmrAppointment) => void;
  onTimeSlotClick: (date: string, time: string) => void;
  onTaskClick?: (task: Task) => void;
}

const START_HOUR = 0; // 0 AM
const END_HOUR = 23; // 12 AM
const HOURS = Array.from({ length: END_HOUR - START_HOUR + 1 }, (_, i) => i + START_HOUR); // 0 AM to 12 AM

const statusColors: Record<string, string> = {
  scheduled: '#3b82f6',
  confirmed: '#22c55e',
  checked_in: '#f59e0b',
  in_progress: '#8b5cf6',
  completed: '#6b7280',
  no_show: '#ef4444',
  cancelled: '#9ca3af',
};

// Check if appointment is joinable (within 15 min before to end of appointment)
// Only video appointments should show JOIN button, not async_message
const isJoinable = (
  apt: EmrAppointment,
  isPractitioner: boolean,
  userPractitionerId?: string
): boolean => {
  if (!isPractitioner) return false;
  // Only show JOIN for video appointments, not async_message
  const isVideoAppointment = (apt as { visit_mode?: string }).visit_mode === 'video';
  if (!isVideoAppointment) return false;
  if (apt.status === 'completed' || apt.status === 'cancelled') return false;
  // Only show join button for the assigned practitioner
  if (userPractitionerId && apt.practitioner_id !== userPractitionerId) return false;

  const now = new Date();
  const start = new Date(apt.start_time);
  const windowStart = addMinutes(start, -15);
  const windowEnd = addMinutes(start, apt.duration || 30);

  return isWithinInterval(now, { start: windowStart, end: windowEnd });
};

const DayView: React.FC<DayViewProps> = ({
  date,
  appointments,
  externalEvents = [],
  tasks = [],
  onAppointmentClick,
  onTimeSlotClick,
  onTaskClick,
}) => {
  const navigate = useNavigate();
  const { isPractitioner } = useClinic();
  const { user } = useAuth();
  const dateStr = format(date, 'yyyy-MM-dd');

  const handleQuickJoin = (e: React.MouseEvent, apt: EmrAppointment) => {
    e.stopPropagation();
    const route = isPractitioner
      ? `/practitioner-portal/telehealth/call/${apt.id}`
      : `/clinic/telehealth/call/${apt.id}`;
    navigate(route);
  };

  // Get appointments for this day
  const dayAppointments = appointments.filter(apt => {
    const aptDate = format(new Date(apt.start_time), 'yyyy-MM-dd');
    return aptDate === dateStr;
  });

  // Get external events for this day (exclude 'free')
  const dayExternalEvents = externalEvents.filter(evt => {
    if (evt.status === 'free') return false;
    const evtDate = format(new Date(evt.start), 'yyyy-MM-dd');
    return evtDate === dateStr;
  });
  const allDayExternalEvents = dayExternalEvents.filter(e => e.isAllDay);
  const timedExternalEvents = dayExternalEvents.filter(e => !e.isAllDay);

  // Get tasks for this day
  const dayTasks = tasks.filter(task => task.due_date === dateStr);

  // Calculate position/height for external events
  const getExternalEventStyle = (evt: CalendarEvent) => {
    const start = new Date(evt.start);
    const end = new Date(evt.end);
    const hour = start.getHours();
    const minute = start.getMinutes();

    let top: number;
    if (hour < START_HOUR) {
      top = 0;
    } else {
      top = ((hour - START_HOUR) * 60 + minute) * (60 / 60);
    }

    const durationMinutes = Math.max((end.getTime() - start.getTime()) / 60000, 15);
    const height = (durationMinutes / 60) * 60;

    return {
      top: `${top}px`,
      height: `${Math.max(height, 30)}px`,
    };
  };

  // Detect overlapping appointments and assign columns
  const getOverlapLayout = (apts: EmrAppointment[]) => {
    if (apts.length === 0) return new Map<string, { column: number; totalColumns: number }>();

    // Sort by start time, then by duration (longer first for better visual stacking)
    const sorted = [...apts].sort((a, b) => {
      const startDiff = new Date(a.start_time).getTime() - new Date(b.start_time).getTime();
      if (startDiff !== 0) return startDiff;
      return (b.duration || 30) - (a.duration || 30);
    });

    const layout = new Map<string, { column: number; totalColumns: number }>();
    const columns: { apt: EmrAppointment; start: number; end: number }[][] = [];

    // Assign columns - appointments go to first column where they don't overlap
    for (const apt of sorted) {
      const aptStart = new Date(apt.start_time).getTime();
      const aptEnd = new Date(apt.end_time).getTime();

      let assignedColumn = -1;
      for (let c = 0; c < columns.length; c++) {
        // Check if appointment fits in this column (no overlap with any existing)
        const canFit = columns[c].every(slot => aptStart >= slot.end || aptEnd <= slot.start);
        if (canFit) {
          assignedColumn = c;
          columns[c].push({ apt, start: aptStart, end: aptEnd });
          break;
        }
      }

      if (assignedColumn === -1) {
        assignedColumn = columns.length;
        columns.push([{ apt, start: aptStart, end: aptEnd }]);
      }

      layout.set(apt.id, { column: assignedColumn, totalColumns: 0 });
    }

    // Now calculate totalColumns for each appointment based on concurrent overlaps
    for (const apt of sorted) {
      const aptStart = new Date(apt.start_time).getTime();
      const aptEnd = new Date(apt.end_time).getTime();

      // Find max concurrent appointments during this appointment's time range
      let maxConcurrent = 0;
      for (const col of columns) {
        const hasOverlap = col.some(slot => aptStart < slot.end && aptEnd > slot.start);
        if (hasOverlap) maxConcurrent++;
      }

      const existing = layout.get(apt.id)!;
      layout.set(apt.id, { ...existing, totalColumns: Math.max(maxConcurrent, 1) });
    }

    return layout;
  };

  const overlapLayout = getOverlapLayout(dayAppointments);

  // Helper to calculate position and height
  const getAppointmentStyle = (apt: EmrAppointment) => {
    const start = new Date(apt.start_time);
    const end = new Date(apt.end_time);
    const hour = start.getHours();
    const minute = start.getMinutes();

    // Calculate position relative to START_HOUR (6 AM)
    // Handle appointments before START_HOUR by positioning at top (0px)
    // Handle appointments after END_HOUR by positioning at bottom
    let top: number;
    if (hour < START_HOUR) {
      // Appointment before visible range - position at top
      top = 0;
    } else if (hour > END_HOUR || (hour === END_HOUR && minute > 0)) {
      // Appointment after visible range - position at bottom
      const totalHeight = (END_HOUR - START_HOUR + 1) * 60; // Total height in pixels
      top = totalHeight;
    } else {
      // Normal case - within visible range
      top = ((hour - START_HOUR) * 60 + minute) * (60 / 60); // 60px per hour
    }

    // Calculate height
    const durationMinutes = apt.duration || Math.round((end.getTime() - start.getTime()) / 60000);
    let height = (durationMinutes / 60) * 60; // 60px per hour

    // Adjust height for appointments that extend beyond visible range
    if (hour < START_HOUR) {
      // If starts before visible range, adjust height
      const visibleStart = START_HOUR * 60;
      const appointmentStartMinutes = hour * 60 + minute;
      const visibleDuration = durationMinutes - (visibleStart - appointmentStartMinutes);
      height = Math.max((visibleDuration / 60) * 60, 40);
    } else if (hour > END_HOUR || (hour === END_HOUR && minute > 0)) {
      // If starts after visible range, show minimal height at bottom
      height = 40;
    } else {
      // Check if appointment extends beyond visible range
      const appointmentEndHour = end.getHours();
      const appointmentEndMinute = end.getMinutes();
      if (
        appointmentEndHour > END_HOUR ||
        (appointmentEndHour === END_HOUR && appointmentEndMinute > 0)
      ) {
        // Appointment extends beyond visible range
        const visibleEnd = (END_HOUR + 1) * 60; // End of visible range in minutes from midnight
        const appointmentStartMinutes = hour * 60 + minute;
        const visibleDuration = visibleEnd - appointmentStartMinutes;
        height = Math.max((visibleDuration / 60) * 60, 40);
      }
    }

    // Get column layout for overlapping appointments
    const layoutInfo = overlapLayout.get(apt.id) || { column: 0, totalColumns: 1 };
    const { column, totalColumns } = layoutInfo;

    // Calculate width and left position based on columns
    const columnWidth = (100 - 2) / totalColumns; // 2% total padding
    const left = 1 + column * columnWidth; // 1% left margin
    const width = columnWidth - 1; // 1% gap between columns

    return {
      top: `${top}px`,
      height: `${Math.max(height, 40)}px`, // Min height 40px for better clickability
      left: `${left}%`,
      width: `${width}%`,
    };
  };

  return (
    <div className='flex h-full flex-col'>
      {/* Header */}
      <div className='flex border-b border-slate-200' style={{ backgroundColor: '#f8f3ed' }}>
        <div className='w-20 flex-shrink-0' />
        <div className='flex-1 px-2 py-2.5 text-center'>
          <span className='text-sm font-semibold text-slate-700'>{format(date, 'EEEE, MMMM d, yyyy')}</span>
        </div>
      </div>

      {/* All-day external events */}
      {allDayExternalEvents.length > 0 && (
        <div className='flex border-b border-slate-200 bg-white'>
          <div className='w-20 flex-shrink-0 flex items-center justify-end pr-3'>
            <span className='text-[10px] font-medium text-slate-400'>All day</span>
          </div>
          <div className='flex-1 border-l border-slate-100 px-2 py-1.5 space-y-1'>
            {allDayExternalEvents.map(evt => {
              const isTentative = evt.status === 'tentative';
              return (
                <div
                  key={`ext-allday-${evt.id}`}
                  className={cn(
                    'flex items-center gap-1 rounded-md px-2 py-1 text-xs',
                    isTentative ? 'bg-amber-50 border border-dashed border-amber-300' : 'bg-slate-100 border border-dashed border-slate-300',
                  )}
                >
                  <CalendarDays className={cn('h-3 w-3 flex-shrink-0', isTentative ? 'text-amber-500' : 'text-slate-400')} />
                  <span className={cn('font-medium', isTentative ? 'text-amber-700' : 'text-slate-500')}>
                    {evt.title || (isTentative ? 'Tentative' : 'Busy')} (All day)
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Tasks banner */}
      {dayTasks.length > 0 && (
        <div className='flex border-b border-orange-100 bg-white'>
          <div className='w-20 flex-shrink-0 flex items-center justify-end pr-3'>
            <span className='text-[10px] font-medium text-orange-400'>Tasks</span>
          </div>
          <div className='flex-1 border-l border-slate-100 px-2 py-1.5 space-y-1'>
            {dayTasks.map(task => {
              const priorityColor = taskPriorityColors[task.priority] || '#f97316';
              const isCompleted = (task.effective_status || task.status) === 'completed';
              return (
                <div
                  key={`task-${task.task_id}`}
                  className={cn(
                    'flex items-center gap-1.5 rounded-md px-2 py-1.5 text-xs cursor-pointer hover:opacity-80 transition-opacity',
                    isCompleted && 'opacity-60',
                  )}
                  style={{
                    backgroundColor: '#f9731612',
                    borderLeft: `3px solid ${priorityColor}`,
                  }}
                  onClick={() => onTaskClick?.(task)}
                >
                  <ClipboardList className='h-3 w-3 flex-shrink-0 text-orange-500' />
                  <span className={cn('font-medium text-slate-700 truncate', isCompleted && 'line-through')}>
                    {task.title}
                  </span>
                  {task.patient && (
                    <span className='text-[10px] text-slate-400 truncate ml-1'>
                      — {[task.patient.first_name, task.patient.middle_name, task.patient.last_name].filter(Boolean).join(' ')}
                    </span>
                  )}
                  <span
                    className='ml-auto rounded-full px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide flex-shrink-0'
                    style={{ backgroundColor: priorityColor + '20', color: priorityColor }}
                  >
                    {task.priority}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Time slots */}
      <div className='flex-1 overflow-y-auto'>
        <div className='flex'>
          {/* Time labels */}
          <div className='w-20 flex-shrink-0'>
            {HOURS.map(hour => (
              <div
                key={hour}
                className='h-[60px] border-b border-slate-100 pr-3 pt-1 text-right text-[11px] font-medium text-slate-400'
              >
                {format(new Date().setHours(hour, 0, 0, 0), 'h a')}
              </div>
            ))}
          </div>

          {/* Appointment column */}
          <div className='relative flex-1 border-l border-slate-100'>
            {/* Grid lines */}
            {HOURS.map(hour => (
              <div
                key={hour}
                className='h-[60px] cursor-pointer border-b border-slate-100 transition-colors hover:bg-brand-teal/[0.03]'
                onClick={() => onTimeSlotClick(dateStr, `${hour.toString().padStart(2, '0')}:00`)}
              />
            ))}

            {/* External events (behind appointments) */}
            {timedExternalEvents.map(evt => {
              const style = getExternalEventStyle(evt);
              const isTentative = evt.status === 'tentative';
              return (
                <div
                  key={`ext-${evt.id}`}
                  className={cn(
                    'absolute left-1 right-1 rounded-md px-2 py-1 text-xs pointer-events-none',
                    isTentative
                      ? 'bg-amber-50/80 border border-dashed border-amber-300'
                      : 'bg-slate-100/80 border border-dashed border-slate-300',
                  )}
                  style={style}
                >
                  <div className='flex items-center gap-1 truncate'>
                    <CalendarDays className={cn('h-3 w-3 flex-shrink-0', isTentative ? 'text-amber-500' : 'text-slate-400')} />
                    <span className={cn('truncate font-medium', isTentative ? 'text-amber-600' : 'text-slate-500')}>
                      {format(new Date(evt.start), 'h:mm a')} – {format(new Date(evt.end), 'h:mm a')}
                    </span>
                    <span className={cn('truncate text-[10px]', isTentative ? 'text-amber-500' : 'text-slate-400')}>
                      {evt.title || (isTentative ? 'Tentative' : 'Busy')}
                    </span>
                  </div>
                </div>
              );
            })}

            {/* Appointments */}
            {dayAppointments.map(apt => {
              const start = new Date(apt.start_time);
              const hour = start.getHours();
              const minute = start.getMinutes();

              // Check if appointment is outside visible range
              const isBeforeVisibleRange = hour < START_HOUR;
              const isAfterVisibleRange = hour > END_HOUR || (hour === END_HOUR && minute > 0);
              const isOutsideVisibleRange = isBeforeVisibleRange || isAfterVisibleRange;

              const style = getAppointmentStyle(apt);
              const color =
                apt.color || apt.serviceType?.color || statusColors[apt.status] || '#4A8A9A';
              const canJoin = isJoinable(apt, isPractitioner, user?.practitionerId);
              const layoutInfo = overlapLayout.get(apt.id);
              const totalCols = layoutInfo?.totalColumns || 1;
              const isNarrow = totalCols > 2;
              const isVeryNarrow = totalCols > 3;

              return (
                <div
                  key={apt.id}
                  className={cn(
                    'group absolute cursor-pointer overflow-hidden rounded-md shadow-sm transition-all',
                    'hover:z-30 hover:min-w-[180px] hover:scale-[1.01] hover:shadow-md',
                    isVeryNarrow ? 'p-1' : 'p-2',
                    canJoin && 'z-10 ring-2 ring-brand-teal ring-offset-1',
                    isOutsideVisibleRange && 'border-dashed opacity-75'
                  )}
                  style={{
                    ...style,
                    backgroundColor: color + '18',
                    borderLeft: `3px solid ${color}`,
                    zIndex: canJoin ? 10 : 1,
                  }}
                  onClick={() => onAppointmentClick(apt)}
                  title={`${apt.patient_name}\n${apt.appointment_type || apt.serviceType?.name || ''}\n${format(new Date(apt.start_time), 'h:mm a')} - ${format(new Date(apt.end_time), 'h:mm a')}${isOutsideVisibleRange ? `\n(Outside visible range)` : ''}`}
                >
                  <div
                    className={cn(
                      'flex items-center gap-1 font-medium text-slate-700',
                      isVeryNarrow ? 'text-[10px]' : 'text-xs'
                    )}
                  >
                    {(apt as { visit_mode?: string }).visit_mode === 'video' && (
                      <Video
                        className={cn(
                          'flex-shrink-0 text-blue-500',
                          isVeryNarrow ? 'h-2.5 w-2.5' : 'h-3 w-3'
                        )}
                      />
                    )}
                    {(apt as { visit_mode?: string }).visit_mode === 'async_message' && (
                      <MessageCircle
                        className={cn(
                          'flex-shrink-0 text-indigo-500',
                          isVeryNarrow ? 'h-2.5 w-2.5' : 'h-3 w-3'
                        )}
                      />
                    )}
                    {(apt as { visit_mode?: string }).visit_mode === 'in_person' && (
                      <Building2
                        className={cn(
                          'flex-shrink-0 text-brand-teal',
                          isVeryNarrow ? 'h-2.5 w-2.5' : 'h-3 w-3'
                        )}
                      />
                    )}
                    <span className='truncate'>{apt.patient_name}</span>
                  </div>

                  {/* Always show time, conditionally show type */}
                  <div
                    className={cn(
                    'truncate text-slate-500',
                    isVeryNarrow ? 'text-[9px]' : 'text-[10px]'
                    )}
                  >
                    {format(new Date(apt.start_time), 'h:mm a')}
                    {!isNarrow && ` - ${format(new Date(apt.end_time), 'h:mm a')}`}
                  </div>

                  {/* Show type only when not narrow */}
                  {!isNarrow && (apt.appointment_type || apt.serviceType?.name) && (
                    <div className='truncate text-[10px] text-slate-400'>
                      {apt.appointment_type || apt.serviceType?.name}
                    </div>
                  )}

                  {/* Expanded content on hover for narrow appointments */}
                  {isNarrow && (
                    <div className='mt-1 hidden text-[10px] text-slate-400 group-hover:block'>
                      <div className='truncate'>
                        {apt.appointment_type || apt.serviceType?.name}
                      </div>
                      <div>
                        {format(new Date(apt.start_time), 'h:mm a')} -{' '}
                        {format(new Date(apt.end_time), 'h:mm a')}
                      </div>
                    </div>
                  )}

                  {canJoin && (
                    <button
                      onClick={e => handleQuickJoin(e, apt)}
                      className={cn(
                        'mt-1 flex w-full items-center justify-center gap-1 rounded bg-brand-teal font-bold text-white transition-colors hover:bg-brand-teal/90',
                        isVeryNarrow ? 'py-0.5 text-[9px]' : 'px-2 py-1 text-xs'
                      )}
                    >
                      <Video className={isVeryNarrow ? 'h-2.5 w-2.5' : 'h-3 w-3'} />
                      {isVeryNarrow ? 'JOIN' : 'JOIN NOW'}
                    </button>
                  )}

                  {/* One-click Start / Resume / Open Visit. Hidden for
                      cancelled / no_show / rescheduled — VisitActionButton
                      returns null in those cases. */}
                  {!isVeryNarrow && (
                    <div className='mt-1 flex justify-end' onClick={e => e.stopPropagation()}>
                      <VisitActionButton appointment={apt} variant='icon' stopPropagation />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DayView;
